import React from 'react';
import {
  AutocompleteInput,
  ChipField,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  DeleteButton,
  Edit,
  EditButton,
  Filter,
  ReferenceField,
  ReferenceInput,
  required,
  SelectInput,
  TextField,
  TextInput,
  useResourceContext,
} from 'react-admin';
import HNForm from '../components/HNForm';
import HNList from '../components/HNList';
import { useNavigate } from 'react-router-dom';
import { useWatch } from 'react-hook-form';

const ActivationFilter: React.FC<any> = (props) => (
  <Filter {...props}>
    <TextInput style={{ minWidth: '300px' }} source="search" label="Search" alwaysOn />
    <ReferenceInput source="access_id" reference="access" label="Access" perPage={10000} alwaysOn>
      <SelectInput optionText="name" resettable />
    </ReferenceInput>
  </Filter>
);

const rowStyle = (record: any) => (!!record.in_app_purchase_activation ? { backgroundColor: '#643DB326' } : undefined);

export const ActivationList: React.FC<any> = (props) => (
  <HNList {...props} filters={<ActivationFilter />} sort={{ field: 'activated_at', order: 'DESC' }}>
    <Datagrid rowStyle={rowStyle}>
      <TextField source="profile.email" label="Profile email" />
      <TextField source="profile.first_name" label="First name" />
      <TextField source="profile.last_name" label="Last name" />
      <TextField source="email" label="Activation email" />
      <TextField
        source="profile.anonymous_id"
        label="Anonymous ID"
        emptyText="-"
        sx={{
          display: 'block',
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        onClick={(e: any) => {
          const text = e.currentTarget.textContent;
          if (text && text !== '-') {
            navigator.clipboard.writeText(text);
          }
        }}
      />
      <DateField source="activated_at" showTime />
      <DateField source="expiration" />
      <ReferenceField reference="access" source="access.id" label="Access">
        <ChipField source="name" />
      </ReferenceField>
      <EditButton />
      <DeleteButton />
    </Datagrid>
  </HNList>
);

const validateEmail = required();

const ActivationTitle: React.FC<any> = ({ record }) => (
  <span>
    {record && record.access && record.access.name ? `Subscription "${record.access.name}"` : 'Create Subscription'}
  </span>
);

export const ActivationCreate: React.FC<any> = (props) => {
  const navigate = useNavigate();
  const resource = useResourceContext();

  return (
    <Create
      title={<ActivationTitle />}
      {...props}
      mutationOptions={{
        onSuccess: () => {
          navigate(`/${resource}`);
        },
      }}>
      <HNForm {...props}>
        <TextInput source="email" validate={validateEmail} />
        <ReferenceInput
          source="access_id"
          reference="access"
          label="Access"
          perPage={10000}
          sort={{ field: 'name', order: 'ASC' }}>
          <SelectInput optionText="name" />
        </ReferenceInput>
      </HNForm>
    </Create>
  );
};

const ProfileIdDisplay: React.FC = () => {
  const profileId = useWatch({ name: 'profile_id' });
  return (
    <div style={{ margin: '1em 0', color: '#888' }}>
      <strong>Profile ID:</strong> {profileId || '-'}
    </div>
  );
};

export const ActivationEdit: React.FC<any> = (props) => (
  <Edit title={<ActivationTitle />} {...props}>
    <HNForm {...props}>
      <ReferenceInput label="Access" source="access_id" reference="access" perPage={10000}>
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ProfileIdDisplay />
      <ReferenceInput
        label="Profile"
        source="profile_id"
        reference="profile"
        sort={{ field: 'created_at', order: 'DESC' }}
        perPage={300}>
        <AutocompleteInput optionText="email" filterToQuery={(searchText: string) => ({ search: searchText })} />
      </ReferenceInput>
      <TextInput source="email" />
      <DateTimeInput source="expiration" />
      <TextInput label="Anonymous ID" source="profile.anonymous_id" disabled />
    </HNForm>
  </Edit>
);
